import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"

// 08 Sep 2026
import EtContentEditorLight2
  from "@/utils/EtContentEditorLight2"

import {
  useCreateNewOrgIntentionBoardMutation,
} from "@/features/orgIntentionBoards/orgIntentionBoardsApiSlice"

import {
  useGetOrganizationsQuery,
} from "@/pages/admin/organizations/organizationsApiSlice"

const EtCreateOrgIntentionBoard = ({
  setActiveAdminView,
}) => {

  // First Created - 04 Sep 2026
  // Author - Atul Pandey
  // Purpose - To create a Trinity Intention Board.
  // Updated - 08 Sep 2026 -  Rich text editor refinement.

  // Debug
  // console.log('inside EtCreateOrgIntentionBoard...')

  useTitle("Create Intention Board")

  const navigate = useNavigate()

  /*
  |--------------------------------------------------------------------------
  | 1 - Create Mutation
  |--------------------------------------------------------------------------
  */

  const [
    createNewOrgIntentionBoard,
    {
      isLoading,
      isSuccess,
      isError,
      error,
    },
  ] =
    useCreateNewOrgIntentionBoardMutation()

  /*
  |--------------------------------------------------------------------------
  | 2 - Get Organizations
  |--------------------------------------------------------------------------
  */

  const {
    data:
      organizationsData,

    isLoading:
      isLoadingOrganizations,

    isError:
      isOrganizationsError,

    error:
      organizationsError,

  } =
    useGetOrganizationsQuery()

  /*
  |--------------------------------------------------------------------------
  | 3 - Normalize Organizations
  |--------------------------------------------------------------------------
  */

  const organizations =
    organizationsData?.ids?.map(
      id =>
        organizationsData
          ?.entities[id]
    ) || []

  /*
  |--------------------------------------------------------------------------
  | 4 - Initial Form State
  |--------------------------------------------------------------------------
  */

  const initialFormState = {

    orgId:
      "",

    trailId:
      "",

    boardName:
      "",

    publicSlug:
      "",

    orgCode:
      "",

    passcode:
      "",

    headline:
      "",

    boardNarrative:
      "",

    closingNarrative:
      "",

    responseDeadline:
      "",

    timezone:
      "America/Los_Angeles",

    boardStatus:
      "draft",

    isActive:
      true,

    allowLateResponses:
      false,

    invitationEmailSubject:
      "",

    invitationEmailBody:
      "",

  }

  /*
  |--------------------------------------------------------------------------
  | 5 - Form State
  |--------------------------------------------------------------------------
  */

  const [
    formData,
    setFormData,
  ] =
    useState(
      initialFormState
    )

  /*
  |--------------------------------------------------------------------------
  | 6 - Success
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (
      isSuccess
    ) {

      setFormData(
        initialFormState
      )

      if (
        setActiveAdminView
      ) {

        setActiveAdminView(
          "orgIntentionBoards"
        )

      } else {

        navigate(
          "/orgIntentionBoards"
        )

      }

    }

  }, [isSuccess])

  /*
  |--------------------------------------------------------------------------
  | 7 - Handle Change
  |--------------------------------------------------------------------------
  */

  const handleChange =
    e => {

      const {
        name,
        value,
        type,
        checked,
      } =
        e.target

      setFormData(
        prevState => ({

          ...prevState,

          [name]:
            type ===
            "checkbox"
              ? checked
              : value,

        })
      )

    }


/*
|--------------------------------------------------------------------------
| Board Narrative Change - 08 Sep 2026
|--------------------------------------------------------------------------
*/

const handleBoardNarrativeChange =
  value => {

    setFormData(
      prevState => ({

        ...prevState,

        boardNarrative:
          value,

      })
    )

  }

  /*
  |--------------------------------------------------------------------------
  | 8 - Handle Submit
  |--------------------------------------------------------------------------
  */

  const handleSubmit =
    async e => {

      e.preventDefault()

      const intentionBoardData = {

        orgId:
          formData.orgId,

        trailId:
          formData.trailId ||
          null,

        boardName:
          formData.boardName,

        publicSlug:
          formData.publicSlug,

        orgCode:
          formData.orgCode,

        passcode:
          formData.passcode,

        headline:
          formData.headline,

        boardNarrative:
          formData.boardNarrative,

        closingNarrative:
          formData.closingNarrative,

        responseDeadline:
          formData.responseDeadline,

        timezone:
          formData.timezone,

        boardStatus:
          formData.boardStatus,

        isActive:
          formData.isActive,

        allowLateResponses:
          formData.allowLateResponses,

        invitationEmailSubject:
          formData.invitationEmailSubject,

        invitationEmailBody:
          formData.invitationEmailBody,

      }

      try {

        await createNewOrgIntentionBoard(
          intentionBoardData
        )
          .unwrap()

      } catch (
        error
      ) {

        console.error(
          error
        )

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 9 - Handle Cancel
  |--------------------------------------------------------------------------
  */

  const handleCancel =
    () => {

      if (
        setActiveAdminView
      ) {

        setActiveAdminView(
          "orgIntentionBoards"
        )

      } else {

        navigate(
          "/orgIntentionBoards"
        )

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 10 - Handle Clear
  |--------------------------------------------------------------------------
  */

  const handleClear =
    () => {

      setFormData(
        initialFormState
      )

    }

  return (

    <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black">

      {/* 1 - Loading */}

      {isLoading && (
        <PulseLoader
          color={"#ea580c"}
        />
      )}

      {isLoadingOrganizations && (
        <PulseLoader
          color={"#ea580c"}
          size={6}
        />
      )}

      {/* 2 - Errors */}

      {isError && (

        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>

      )}

      {isOrganizationsError && (

        <p className="text-orange-700 font-semi-bold py-2">
          {organizationsError
            ?.data
            ?.message ||
            "Error loading organizations"}
        </p>

      )}

      <div className="bg-white p-1 m-1 rounded">

        <p className="p-1 m-1 font-bold font-poppins text-xl">
          Add an Intention Board
        </p>

        <div className="bg-gray-200 rounded m-1 p-1">

          <form
            onSubmit={handleSubmit}
            className="m-1 p-1 text-xs font-poppins"
          >

            {/* Organization */}

            <div id="orgId">

              <select
                name="orgId"
                value={formData.orgId}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              >

                <option value="">
                  Select Organization
                </option>

                {organizations
                  ?.filter(
                    organization =>
                      organization
                        ?.active !==
                      false
                  )
                  ?.sort(
                    (
                      a,
                      b
                    ) =>
                      (
                        a
                          ?.organizationName ||
                        ""
                      )
                        .localeCompare(
                          b
                            ?.organizationName ||
                          ""
                        )
                  )
                  ?.map(
                    organization => (

                      <option
                        key={
                          organization
                            ?._id
                        }
                        value={
                          organization
                            ?._id
                        }
                      >
                        {
                          organization
                            ?.organizationName
                        }
                      </option>

                    )
                  )}

              </select>

              <p className="text-[9px] text-gray-500 mx-2 px-2">
                Select the organization for which this Intention Board
                is being created.
              </p>

            </div>

            {/* Trail */}

            <div id="trailId">

              <input
                type="text"
                name="trailId"
                placeholder="Enter Trail ID, if applicable"
                value={formData.trailId}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />

              <p className="text-[9px] text-gray-500 mx-2 px-2">
                Optional. Leave blank if this Intention Board is not linked
                to a specific Trinity Trail.
              </p>

            </div>

            {/* Board Name */}

            <div id="boardName">

              <input
                type="text"
                name="boardName"
                placeholder="Enter the Intention Board name"
                value={formData.boardName}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />

            </div>

            {/* Public Slug */}

            <div id="publicSlug">

              <input
                type="text"
                name="publicSlug"
                placeholder="Enter public slug, for example catalyst-intention"
                value={formData.publicSlug}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />

              <p className="text-[9px] text-gray-500 mx-2 px-2">
                This will be used in the public Intention Board URL.
              </p>

            </div>

            {/* Organization Code */}

            <div id="orgCode">

              <input
                type="text"
                name="orgCode"
                placeholder="Enter organization access code"
                value={formData.orgCode}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />

            </div>

            {/* Passcode */}

            <div id="passcode">

              <input
                type="text"
                name="passcode"
                placeholder="Enter access passcode"
                value={formData.passcode}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />

            </div>

            {/* Headline */}

            <div id="headline">

              <input
                type="text"
                name="headline"
                placeholder="Enter Intention Board headline"
                value={formData.headline}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />

            </div>

            {/*
            |--------------------------------------------------------------------------
            | Board Narrative - Before You Begin
            |--------------------------------------------------------------------------
            */}

            <div
            id="boardNarrative"
            className="m-1 p-1"
            >

            <p className="font-bold text-xs mx-1 mb-2">
                Before You Begin
            </p>

            <EtContentEditorLight2
                editorKey={
                "createOrgIntentionBoardNarrative"
                }
                value={
                formData
                    .boardNarrative
                }
                handleChange={
                handleBoardNarrativeChange
                }
                placeholderText={
                "Enter the opening narrative for nominated leaders..."
                }
            />

            </div>

            {/* Board Narrative */}

            {/* <div id="boardNarrative">

              <textarea
                name="boardNarrative"
                placeholder="Enter the opening narrative for nominated leaders"
                value={formData.boardNarrative}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
                rows={6}
              />

            </div> */}

            {/* Closing Narrative */}

            <div id="closingNarrative">

              <textarea
                name="closingNarrative"
                placeholder="Enter the closing narrative shown after the Intention Board"
                value={formData.closingNarrative}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
                rows={4}
              />

            </div>

            {/* Response Deadline */}

            <div
              id="responseDeadline"
              className="m-1 p-1"
            >

              <p className="font-bold mx-1 mb-1">
                Response Deadline
              </p>

              <input
                type="datetime-local"
                name="responseDeadline"
                value={formData.responseDeadline}
                onChange={handleChange}
                className="w-full rounded p-2"
              />

            </div>

            {/* Timezone */}

            <div id="timezone">

              <select
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              >

                <option value="America/Los_Angeles">
                  US Pacific
                </option>

                <option value="America/Denver">
                  US Mountain
                </option>

                <option value="America/Chicago">
                  US Central
                </option>

                <option value="America/New_York">
                  US Eastern
                </option>

                <option value="Europe/London">
                  UK
                </option>

                <option value="Asia/Kolkata">
                  India
                </option>

              </select>

              <p className="text-[9px] text-gray-500 mx-2 px-2">
                The response deadline will be interpreted in this timezone.
              </p>

            </div>

            {/* Board Status */}

            <div id="boardStatus">

              <select
                name="boardStatus"
                value={formData.boardStatus}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              >

                <option value="draft">
                  Draft
                </option>

                <option value="open">
                  Open
                </option>

                <option value="closed">
                  Closed
                </option>

                <option value="archived">
                  Archived
                </option>

              </select>

            </div>

            {/* Board Controls */}

            <div className="m-1 p-2 bg-white rounded">

              <p className="font-bold mb-2">
                Board Controls
              </p>

              <div className="flex items-center mb-2">

                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="mr-2"
                />

                <label
                  htmlFor="isActive"
                  className="text-xs"
                >
                  Active
                </label>

              </div>

              <div className="flex items-center">

                <input
                  type="checkbox"
                  id="allowLateResponses"
                  name="allowLateResponses"
                  checked={formData.allowLateResponses}
                  onChange={handleChange}
                  className="mr-2"
                />

                <label
                  htmlFor="allowLateResponses"
                  className="text-xs"
                >
                  Allow responses after the deadline
                </label>

              </div>

            </div>

            {/* Invitation Email Subject */}

            <div id="invitationEmailSubject">

              <input
                type="text"
                name="invitationEmailSubject"
                placeholder="Invitation email subject"
                value={formData.invitationEmailSubject}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />

            </div>

            {/* Invitation Email Body */}

            <div id="invitationEmailBody">

              <textarea
                name="invitationEmailBody"
                placeholder="Invitation email body"
                value={formData.invitationEmailBody}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
                rows={5}
              />

            </div>

            {/* Actions */}

            <div
              id="actions"
              className="bg-white m-1 px-1 py-3 rounded"
            >

              <EtBtnFormBasic
                name={"Create"}
                type={"create"}
                handleClick={handleSubmit}
                disabled={
                  isLoading ||
                  isLoadingOrganizations ||
                  !formData.orgId
                }
              />

              <EtBtnFormBasic
                name={"Clear"}
                type={"clear"}
                handleClick={handleClear}
              />

              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />

            </div>

          </form>

        </div>

      </div>

    </div>

  )

}

export default EtCreateOrgIntentionBoard